import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme, THEMES, type ThemeFamilyId } from '@evi/skins';
import { setActiveSkin, recordRecentTheme } from '@/lib/api';
import { toast } from '@/store/toast';

/**
 * Minimal shape the apply handler needs. Both the picker's
 * `DriverPickerEntry` and the themes page's `ThemeCard` satisfy
 * this, so both screens share one apply path.
 */
export interface ApplyableTheme {
  code: string;
  name: string;
  kind: 'family' | 'skin';
  locked: boolean;
  active: boolean;
}

/**
 * Shared apply-theme flow used by the picker and the full Themes page.
 * Handles:
 *   - Family pick: setThemeId + drop skin override + clear server-side
 *     active_skin_code + record recency. Required sequence — an active
 *     skin's themeId wins over setThemeId at the provider level, so
 *     the family flip is invisible unless the skin override is also
 *     cleared (ProfilePage fix at commit dfbba8a).
 *   - Skin pick: setActiveSkin writes recents + active_skin_code in
 *     one round-trip; useSkinSync picks up the change on invalidation.
 *   - Locked pick: no-op here — callers are responsible for routing
 *     locked taps elsewhere (picker → /themes focus; themes page →
 *     HowToEarnSheet).
 *   - Already-active pick: no network traffic, just a neutral toast.
 *
 * Always invalidates ['profile'], ['unlocks'], ['unlocks', 'active-skin']
 * so every consumer (picker inline list, themes page cards, header
 * avatar skin-edge) re-reads the new active state without waiting
 * for staleTime windows to expire.
 */
export function useApplyTheme(): {
  applyTheme: (entry: ApplyableTheme) => Promise<void>;
} {
  const queryClient = useQueryClient();
  const { setThemeId, setSkinOverride } = useTheme();

  const applyTheme = useCallback(
    async (entry: ApplyableTheme): Promise<void> => {
      if (entry.locked) {
        // Caller should have routed this before calling us; shout
        // rather than silently no-op so the bug is loud.
        // eslint-disable-next-line no-console
        console.warn(
          'useApplyTheme: refusing to apply a locked entry',
          entry.code,
        );
        return;
      }

      if (entry.active) {
        toast(`${entry.name} is already active`, { tone: 'neutral' });
        return;
      }

      try {
        if (entry.kind === 'family') {
          // Driver Themes ticket — registry-driven lookup replaces
          // the legacy hardcoded ['default', 'luce'] allow-list. Any
          // family present in THEMES routes through setThemeId; the
          // cast is safe because `in THEMES` narrowed the runtime
          // shape against the registry's keyed type.
          if (entry.code in THEMES) {
            setThemeId(entry.code as ThemeFamilyId);
          }
          // Drop any runtime skin overlay immediately so the family
          // flip paints right away (provider effectiveThemeId
          // formula: skinOverride?.themeId ?? themeId — without the
          // override clear, the family flip stays invisible until
          // /unlocks/active-skin's 60s staleTime expires).
          setSkinOverride(null);
          await setActiveSkin(null);
          await recordRecentTheme(entry.code);
        } else {
          await setActiveSkin(entry.code);
        }

        queryClient.invalidateQueries({ queryKey: ['profile'] });
        queryClient.invalidateQueries({ queryKey: ['unlocks'] });
        queryClient.invalidateQueries({ queryKey: ['unlocks', 'active-skin'] });
        toast(`${entry.name} applied`, { tone: 'accent' });
      } catch (err) {
        toast('Could not apply theme', { tone: 'error' });
        // Console log so QA can see the cause without inventing a
        // second toast state for long error strings.
        // eslint-disable-next-line no-console
        console.error('applyTheme failed', err);
      }
    },
    [queryClient, setSkinOverride, setThemeId],
  );

  return { applyTheme };
}
